import React, { useState, useRef, useEffect } from 'react';
import { 
  Wrench, ChevronDown, ChevronRight, Settings, Play, 
  Trash2, Save, Copy, Eye, EyeOff, ZoomIn, ZoomOut, RotateCw,
  Calculator, Brain, Star, BookOpen, Info, Search, Filter, Grid,
  PanelLeftClose, PanelRightClose, PanelLeftOpen, PanelRightOpen,
  GripVertical, Minus, Maximize2, ArrowLeft
} from 'lucide-react';

const ToolBoxPage = ({ solution, onRun, onBack }) => {
  const [workspace, setWorkspace] = useState([]);
  const [selectedNode, setSelectedNode] = useState(null);
  const [workspaceTransform, setWorkspaceTransform] = useState({
    scale: 0.5,
    translateX: 0,
    translateY: 0
  });
  const [isDragging, setIsDragging] = useState(false);
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 });
  const [collapsedGroups, setCollapsedGroups] = useState({
    analysis: true,
    concepts: true,
    formulas: true,
    values: true,
    steps: true
  });
  const [parameterPanel, setParameterPanel] = useState({
    isVisible: false,
    nodeId: null,
    parameters: {}
  });
  const [groupNodes, setGroupNodes] = useState({}); // Track collapsed group nodes in workspace

  // Panel visibility and sizing states
  const [leftPanelVisible, setLeftPanelVisible] = useState(true);
  const [rightPanelVisible, setRightPanelVisible] = useState(false);
  const [leftPanelWidth, setLeftPanelWidth] = useState(350);
  const [rightPanelWidth, setRightPanelWidth] = useState(350);
  const [isResizingLeft, setIsResizingLeft] = useState(false);
  const [isResizingRight, setIsResizingRight] = useState(false);

  const workspaceRef = useRef(null);
  const leftPanelRef = useRef(null);
  const rightPanelRef = useRef(null);

  // Extract dynamic toolbox content from solution data
  const getToolboxContent = () => {
    if (!solution) return null;

    const graphData = solution.graphData;
    if (!graphData || !graphData.nodes) return null;

    // Categorize nodes by type for toolbox groups
    const analysisPanel = graphData.nodes.filter(node => node.type === 'concepts');
    const mathsConcepts = graphData.nodes.filter(node => node.type === 'concepts');
    const formulasMethods = graphData.nodes.filter(node => node.type === 'formulas');
    const valuesResults = graphData.nodes.filter(node => node.type === 'values');
    const solutionSteps = graphData.nodes.filter(node => node.type === 'steps');

    return {
      analysisPanel,
      mathsConcepts,
      formulasMethods,
      valuesResults,
      solutionSteps
    };
  };

  const toolboxContent = getToolboxContent();

  // Helper function for icon colors
  const getIconColor = (color) => {
    const colorMap = {
      'blue': 'text-blue-600',
      'indigo': 'text-indigo-600',
      'emerald': 'text-emerald-600',
      'amber': 'text-amber-600',
      'violet': 'text-violet-600'
    };
    return colorMap[color] || 'text-gray-600';
  };

  // Dynamic toolbox groups based on solution data
  const getToolboxGroups = () => {
    if (!toolboxContent) {
      // Fallback to static content if no solution data
      return [
        {
          id: 'analysis',
          title: 'Analysis Panel',
          color: 'blue',
          description: 'Mathematical analysis and entity search',
          items: [
            { id: 'search', name: 'Entity Search', type: 'search', description: 'Search mathematical entities' },
            { id: 'filter', name: 'Entity Filter', type: 'filter', description: 'Filter entities by type' },
            { id: 'analysis', name: 'Analysis Panel', type: 'analysis', description: 'Detailed entity analysis' }
          ]
        },
        {
          id: 'concepts',
          title: 'Maths Concept',
          color: 'indigo',
          description: 'Core mathematical ideas and principles',
          items: [
            { id: 'negative-indices', name: 'Negative Indices', type: 'concept', description: 'Understanding negative indices and their properties', formula: 'a⁻ⁿ = 1/aⁿ' },
            { id: 'power-of-power', name: 'Power of Power', type: 'concept', description: 'When raising a power to another power, multiply the indices', formula: '(aᵐ)ⁿ = aᵐⁿ' },
            { id: 'division-powers', name: 'Division of Powers', type: 'concept', description: 'When dividing powers with the same base, subtract the indices', formula: 'aᵐ ÷ aⁿ = aᵐ⁻ⁿ' }
          ]
        },
        {
          id: 'formulas',
          title: 'Formulas & Methods',
          color: 'emerald',
          description: 'Mathematical formulas and solution methods',
          items: [
            { id: 'quadratic-formula', name: 'Quadratic Formula', type: 'formula', description: 'Standard quadratic equation solution', formula: 'x = (-b ± √(b² - 4ac)) / 2a' },
            { id: 'factoring', name: 'Factoring Method', type: 'method', description: 'Factor quadratic expressions', formula: 'ax² + bx + c = (px + q)(rx + s)' }
          ]
        },
        {
          id: 'values',
          title: 'Values & Results',
          color: 'amber',
          description: 'Numerical values and computed results',
          items: [
            { id: 'discriminant', name: 'Discriminant', type: 'value', description: 'Δ = b² - 4ac', formula: 'Δ = b² - 4ac' },
            { id: 'solutions', name: 'Solutions', type: 'value', description: 'x₁ and x₂ values', formula: 'x₁, x₂ = ...' },
            { id: 'final-answer', name: 'Final Answer', type: 'result', description: 'The simplified expression in positive indices', formula: 'x¹¹ / y¹⁷' }
          ]
        },
        {
          id: 'steps',
          title: 'Solution Steps',
          color: 'violet',
          description: 'Step-by-step solution process',
      items: [
        { id: 'step1', name: 'Step 1: Simplify Numerator', type: 'step', description: 'Apply power of power rule to (x⁴y⁻⁵)³', formula: '(x⁴y⁻⁵)³ = x¹²y⁻¹⁵' },
        { id: 'step2', name: 'Step 2: Divide by Denominator', type: 'step', description: 'Divide x¹²y⁻¹⁵ by xy²', formula: 'x¹²y⁻¹⁵ ÷ xy² = x¹¹y⁻¹⁷' },
        { id: 'step3', name: 'Step 3: Convert to Positive Indices', type: 'step', description: 'Convert negative indices to positive using reciprocal', formula: 'x¹¹y⁻¹⁷ = x¹¹/y¹⁷' }
      ]
    }
  ];
    }

    // Use dynamic content from solution data
    return [
      {
        id: 'analysis',
        title: 'Analysis Panel',
        color: 'blue',
        description: 'Mathematical analysis and entity search',
        items: toolboxContent.analysisPanel.map(node => ({
          id: node.id,
          name: node.name,
          type: 'analysis',
          description: node.description,
          formula: node.value
        }))
      },
      {
        id: 'concepts',
        title: 'Maths Concept',
        color: 'indigo',
        description: 'Core mathematical ideas and principles',
        items: toolboxContent.mathsConcepts.map(node => ({
          id: node.id,
          name: node.name,
          type: 'concept',
          description: node.description,
          formula: node.value
        }))
      },
      {
        id: 'formulas',
        title: 'Formulas & Methods',
        color: 'emerald',
        description: 'Mathematical formulas and solution methods',
        items: toolboxContent.formulasMethods.map(node => ({
          id: node.id,
          name: node.name,
          type: 'formula',
          description: node.description,
          formula: node.value
        }))
      },
      {
        id: 'values',
        title: 'Values & Results',
        color: 'amber',
        description: 'Numerical values and computed results',
        items: toolboxContent.valuesResults.map(node => ({
          id: node.id,
          name: node.name,
          type: 'value',
          description: node.description,
          formula: node.value
        }))
      },
      {
        id: 'steps',
        title: 'Solution Steps',
        color: 'violet',
        description: 'Step-by-step solution process',
        items: toolboxContent.solutionSteps.map(node => ({
          id: node.id,
          name: node.name,
          type: 'step',
          description: node.description,
          formula: node.value
        }))
      }
    ];
  };

  const toolboxGroups = getToolboxGroups();

  // Handle drag and drop
  const handleDragStart = (e, item) => {
    e.dataTransfer.setData('application/json', JSON.stringify(item));
  };



  const handleWorkspaceDragOver = (e) => {
    e.preventDefault();
  };

  // Workspace controls
  const handleZoom = (direction) => {
    setWorkspaceTransform(prev => ({
      ...prev,
      scale: Math.max(0.5, Math.min(2, prev.scale + (direction === 'in' ? 0.1 : -0.1)))
    }));
  };

  const handlePan = (e) => {
    if (!isDragging) return;
    
    const deltaX = e.clientX - dragStart.x;
    const deltaY = e.clientY - dragStart.y;
    
    setWorkspaceTransform(prev => ({
      ...prev,
      translateX: prev.translateX + deltaX,
      translateY: prev.translateY + deltaY
    }));
    
    setDragStart({ x: e.clientX, y: e.clientY });
  };

  const handleMouseDown = (e) => {
    setIsDragging(true);
    setDragStart({ x: e.clientX, y: e.clientY });
  };

  const handleMouseUp = () => {
    setIsDragging(false);
  };

  // Node selection and parameter editing
  const handleNodeClick = (node) => {
    setSelectedNode(node);
    setParameterPanel({
      isVisible: true,
      nodeId: node.id,
      parameters: node.parameters || {}
    });
  };

  const handleParameterChange = (key, value) => {
    setParameterPanel(prev => ({
      ...prev,
      parameters: { ...prev.parameters, [key]: value }
    }));
    
    setWorkspace(prev => prev.map(node => 
      node.id === parameterPanel.nodeId 
        ? { ...node, parameters: { ...node.parameters, [key]: value } }
        : node
    ));
  };

  const handleRun = () => {
    if (onRun) {
      onRun(workspace);
    }
  };

  // Toggle group collapse in workspace
  const toggleWorkspaceGroup = (groupId) => {
    setGroupNodes(prev => ({
      ...prev,
      [groupId]: {
        ...prev[groupId],
        isCollapsed: !prev[groupId]?.isCollapsed
      }
    }));
  };

  const toggleGroup = (groupId) => {
    setCollapsedGroups(prev => ({
      ...prev,
      [groupId]: !prev[groupId]
    }));
  };

  // Collapse/Expand all groups
  const collapseAllGroups = () => {
    const allCollapsed = {};
    toolboxGroups.forEach(group => {
      allCollapsed[group.id] = true;
    });
    setCollapsedGroups(allCollapsed);
  };

  const expandAllGroups = () => {
    setCollapsedGroups({});
  };

  // Handle group dragging (drag entire group to workspace)
  const handleGroupDragStart = (e, group) => {
    e.dataTransfer.setData('application/json', JSON.stringify({
      type: 'group',
      group: group,
      items: group.items
    }));
  };

  // Enhanced workspace drop to handle groups
  const handleWorkspaceDrop = (e) => {
    e.preventDefault();
    const itemData = JSON.parse(e.dataTransfer.getData('application/json'));
    const rect = workspaceRef.current.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const y = e.clientY - rect.top;

    if (itemData.type === 'group') {
      // Handle group drop - create connected nodes
      const group = itemData.group;
      const items = itemData.items;
      const spacing = 200; // Space between nodes
      
      const newNodes = items.map((item, index) => ({
        ...item,
        id: `${item.id}-${Date.now()}-${index}`,
        position: { 
          x: x + (index * spacing), 
          y: y + (index * 50) 
        },
        parameters: { ...item.parameters },
        groupId: group.id,
        groupName: group.title,
        isGrouped: true,
        groupIndex: index
      }));

      // Add group container node
      const groupContainerNode = {
        id: `group-${group.id}-${Date.now()}`,
        name: group.title,
        type: 'group-container',
        description: `Collapsed group: ${group.title}`,
        position: { x, y },
        groupId: group.id,
        isCollapsed: false,
        childNodes: newNodes.map(node => node.id)
      };

      setWorkspace(prev => [...prev, groupContainerNode, ...newNodes]);
      setGroupNodes(prev => ({
        ...prev,
        [group.id]: {
          containerId: groupContainerNode.id,
          nodeIds: newNodes.map(node => node.id),
          isCollapsed: false
        }
      }));
    } else {
      // Handle individual item drop
      const newNode = {
        ...itemData,
        id: `${itemData.id}-${Date.now()}`,
        position: { x, y },
        parameters: { ...itemData.parameters }
      };

      setWorkspace(prev => [...prev, newNode]);
    }
  };

  // Panel resize handlers
  const handleLeftResizeStart = (e) => {
    e.preventDefault();
    setIsResizingLeft(true);
    document.addEventListener('mousemove', handleLeftResize);
    document.addEventListener('mouseup', handleLeftResizeEnd);
  };

  const handleLeftResize = (e) => {
    if (!isResizingLeft) return;
    const newWidth = e.clientX;
    const minWidth = 250;
    const maxWidth = 600;
    const clampedWidth = Math.min(maxWidth, Math.max(minWidth, newWidth));
    setLeftPanelWidth(clampedWidth);
  };

  const handleLeftResizeEnd = () => {
    setIsResizingLeft(false);
    document.removeEventListener('mousemove', handleLeftResize);
    document.removeEventListener('mouseup', handleLeftResizeEnd);
  };

  const handleRightResizeStart = (e) => {
    e.preventDefault();
    setIsResizingRight(true);
    document.addEventListener('mousemove', handleRightResize);
    document.addEventListener('mouseup', handleRightResizeEnd);
  };

  const handleRightResize = (e) => {
    if (!isResizingRight) return;
    const newWidth = window.innerWidth - e.clientX;
    const minWidth = 250;
    const maxWidth = 600;
    const clampedWidth = Math.min(maxWidth, Math.max(minWidth, newWidth));
    setRightPanelWidth(clampedWidth);
  };

  const handleRightResizeEnd = () => {
    setIsResizingRight(false);
    document.removeEventListener('mousemove', handleRightResize);
    document.removeEventListener('mouseup', handleRightResizeEnd);
  };

  // Panel toggle handlers
  const toggleLeftPanel = () => {
    setLeftPanelVisible(!leftPanelVisible);
  };

  const toggleRightPanel = () => {
    setRightPanelVisible(!rightPanelVisible);
  };

  return (
    <div className="h-screen flex flex-col bg-gradient-to-br from-gray-50 to-gray-100">
      {/* Header */}
      <div className="bg-white border-b border-gray-200 shadow-sm">
        <div className="px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <button
              onClick={onBack}
              className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
              title="Back to Solver"
            >
              <ArrowLeft className="w-5 h-5 text-gray-600" />
            </button>
            <div>
              <h1 className="text-xl font-bold text-gray-900">Mathematical ToolBox</h1>
              <p className="text-xs text-gray-600">Professional Analysis Workspace</p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <button
              onClick={toggleLeftPanel}
              className="p-2 rounded-lg bg-white border border-gray-200 hover:bg-gray-50 hover:border-gray-300 transition-all shadow-sm"
              title={leftPanelVisible ? "Hide ToolBox" : "Show ToolBox"}
            >
              {leftPanelVisible ? <PanelLeftClose className="w-4 h-4 text-gray-600" /> : <PanelLeftOpen className="w-4 h-4 text-gray-600" />}
            </button>
            <button
              onClick={toggleRightPanel}
              className="p-2 rounded-lg bg-white border border-gray-200 hover:bg-gray-50 hover:border-gray-300 transition-all shadow-sm"
              title={rightPanelVisible ? "Hide Properties" : "Show Properties"}
            >
              {rightPanelVisible ? <PanelRightClose className="w-4 h-4 text-gray-600" /> : <PanelRightOpen className="w-4 h-4 text-gray-600" />}
            </button>
            <div className="w-px h-6 bg-gray-300"></div>
            <button
              onClick={() => handleZoom('in')}
              className="p-2 rounded-lg bg-white border border-gray-200 hover:bg-gray-50 hover:border-gray-300 transition-all shadow-sm"
              title="Zoom In"
            >
              <ZoomIn className="w-4 h-4 text-gray-600" />
            </button>
            <button
              onClick={() => handleZoom('out')}
              className="p-2 rounded-lg bg-white border border-gray-200 hover:bg-gray-50 hover:border-gray-300 transition-all shadow-sm"
              title="Zoom Out"
            >
              <ZoomOut className="w-4 h-4 text-gray-600" />
            </button>
            <button
              onClick={() => setWorkspaceTransform({ scale: 0.5, translateX: 0, translateY: 0 })}
              className="p-2 rounded-lg bg-white border border-gray-200 hover:bg-gray-50 hover:border-gray-300 transition-all shadow-sm"
              title="Reset View"
            >
              <RotateCw className="w-4 h-4 text-gray-600" />
            </button>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="flex-1 flex">
        {/* Left Toolbox Panel */}
        {leftPanelVisible && (
          <div 
            ref={leftPanelRef}
            className="bg-white border-r border-gray-300 shadow-lg flex flex-col relative"
            style={{ width: `${leftPanelWidth}px` }}
          >
            <div className="p-6 border-b border-gray-200 bg-gradient-to-r from-blue-50 to-indigo-50">
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-blue-100 rounded-lg">
                    <Wrench className="w-6 h-6 text-blue-600" />
                  </div>
                  <div>
                    <h2 className="text-lg font-bold text-gray-900">ToolBox</h2>
                    <p className="text-xs text-gray-600 font-medium">Mathematical Tools</p>
                  </div>
                </div>
                <button
                  onClick={toggleLeftPanel}
                  className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                  title="Hide ToolBox"
                >
                  <PanelLeftClose className="w-5 h-5 text-gray-600" />
                </button>
              </div>
              
              {/* Collapse/Expand All Controls */}
              <div className="flex items-center gap-2 mt-4">
                <button
                  onClick={expandAllGroups}
                  className="flex items-center gap-2 px-3 py-2 bg-green-100 hover:bg-green-200 text-green-700 rounded-lg text-sm font-medium transition-colors"
                  title="Expand All Groups"
                >
                  <ChevronDown className="w-4 h-4" />
                  Expand All
                </button>
                <button
                  onClick={collapseAllGroups}
                  className="flex items-center gap-2 px-3 py-2 bg-orange-100 hover:bg-orange-200 text-orange-700 rounded-lg text-sm font-medium transition-colors"
                  title="Collapse All Groups"
                >
                  <ChevronRight className="w-4 h-4" />
                  Collapse All
                </button>
              </div>
            </div>

            <div className="flex-1 overflow-y-auto p-6 space-y-6">
              {toolboxGroups.map(group => {
                const isCollapsed = collapsedGroups[group.id];
                
                return (
                  <div key={group.id} className="bg-white border border-gray-200 rounded-xl shadow-sm hover:shadow-md transition-shadow">
                    <div
                      draggable
                      onDragStart={(e) => handleGroupDragStart(e, group)}
                      className="w-full p-4 flex items-center justify-between text-left hover:bg-gray-50 transition-colors rounded-t-xl cursor-move group"
                    >
                      <div className="flex items-center gap-3">
                        <div className={`p-2 rounded-lg ${getIconColor(group.color).replace('text-', 'bg-').replace('-600', '-100')}`}>
                          {group.id === 'analysis' && <Info className={`w-5 h-5 ${getIconColor(group.color)}`} />}
                          {group.id === 'concepts' && <Brain className={`w-5 h-5 ${getIconColor(group.color)}`} />}
                          {group.id === 'formulas' && <Calculator className={`w-5 h-5 ${getIconColor(group.color)}`} />}
                          {group.id === 'values' && <Star className={`w-5 h-5 ${getIconColor(group.color)}`} />}
                          {group.id === 'steps' && <BookOpen className={`w-5 h-5 ${getIconColor(group.color)}`} />}
                        </div>
                        <div className="flex-1">
                          <div className="flex items-center justify-between">
                            <div>
                              <span className="font-semibold text-gray-900 text-sm">{group.title}</span>
                              <p className="text-xs text-gray-500 mt-1">{group.description}</p>
                            </div>
                            <div className="flex items-center gap-2">
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  toggleGroup(group.id);
                                }}
                                className="p-1 hover:bg-gray-200 rounded transition-colors"
                                title={isCollapsed ? "Expand Group" : "Collapse Group"}
                              >
                                {isCollapsed ? <ChevronRight className="w-4 h-4 text-gray-400" /> : <ChevronDown className="w-4 h-4 text-gray-400" />}
                              </button>
                              <div className="w-2 h-2 bg-gray-300 rounded-full opacity-0 group-hover:opacity-100 transition-opacity"></div>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                    
                    {!isCollapsed && (
                      <div className="p-4 pt-0 space-y-3">
                        {group.items.map(item => (
                          <div
                            key={item.id}
                            draggable
                            onDragStart={(e) => handleDragStart(e, item)}
                            className="p-4 bg-gradient-to-r from-gray-50 to-white rounded-lg border border-gray-200 cursor-move hover:shadow-md hover:border-gray-300 transition-all duration-200 group"
                          >
                            <div className="flex items-start justify-between">
                              <div className="flex-1">
                                <div className="text-xs font-semibold text-gray-900 mb-2 leading-tight">{item.name}</div>
                                <div className="text-sm text-gray-600 mb-3 leading-relaxed">{item.description}</div>
                                {item.formula && (
                                  <div className="text-sm text-blue-700 font-mono bg-blue-50 px-3 py-2 rounded-lg border border-blue-200">
                                    {item.formula}
                                  </div>
                                )}
                              </div>
                              <div className="ml-3 opacity-0 group-hover:opacity-100 transition-opacity">
                                <div className="w-2 h-2 bg-gray-300 rounded-full"></div>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
            
            {/* Left Panel Resize Handle */}
            <div
              className="absolute right-0 top-0 bottom-0 w-1 bg-gray-300 hover:bg-blue-500 cursor-col-resize flex items-center justify-center"
              onMouseDown={handleLeftResizeStart}
            >
              <GripVertical className="w-3 h-3 text-gray-500" />
            </div>
          </div>
        )}

        {/* Left Panel Toggle Button */}
        {!leftPanelVisible && (
          <button
            onClick={toggleLeftPanel}
            className="absolute left-0 top-20 z-10 p-2 bg-white border border-gray-300 rounded-r-lg shadow-lg hover:bg-gray-50 transition-colors"
            title="Show ToolBox"
          >
            <PanelLeftOpen className="w-5 h-5 text-gray-600" />
          </button>
        )}

        {/* Center Workspace */}
        <div className="flex-1 flex flex-col bg-white shadow-lg">
          <div className="flex-1 relative overflow-hidden bg-gradient-to-br from-gray-50 to-gray-100">
            <div
              ref={workspaceRef}
              className="w-full h-full relative cursor-move"
              onDrop={handleWorkspaceDrop}
              onDragOver={handleWorkspaceDragOver}
              onMouseDown={handleMouseDown}
              onMouseMove={handlePan}
              onMouseUp={handleMouseUp}
              onMouseLeave={handleMouseUp}
            >
              {workspace.length === 0 ? (
                <div className="absolute inset-0 flex items-center justify-center">
                  <div className="text-center text-gray-500 max-w-md">
                    <div className="p-8 bg-white rounded-2xl shadow-lg border border-gray-200">
                      <Grid className="w-24 h-24 mx-auto mb-6 opacity-40 text-gray-400" />
                      <h3 className="text-2xl font-semibold text-gray-700 mb-4">Welcome to Your Workspace</h3>
                      <p className="text-gray-600 mb-6 leading-relaxed text-lg">
                        Drag mathematical tools from the left panel to start building your analysis. 
                        Each tool can be positioned, resized, and configured.
                      </p>
                      <div className="flex items-center justify-center gap-4 text-sm text-gray-500">
                        <div className="flex items-center gap-2">
                          <div className="w-3 h-3 bg-blue-400 rounded-full"></div>
                          <span>Click and drag to pan</span>
                        </div>
                        <div className="flex items-center gap-2">
                          <div className="w-3 h-3 bg-green-400 rounded-full"></div>
                          <span>Use zoom controls</span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              ) : (
                <div
                  style={{
                    transform: `scale(${workspaceTransform.scale}) translate(${workspaceTransform.translateX}px, ${workspaceTransform.translateY}px)`,
                    transformOrigin: '0 0'
                  }}
                >
                  {workspace.map(node => {
                    // Skip child nodes if their group is collapsed
                    if (node.isGrouped && node.groupId) {
                      const groupInfo = groupNodes[node.groupId];
                      if (groupInfo && groupInfo.isCollapsed) {
                        return null;
                      }
                    }

                    // Handle group container nodes
                    if (node.type === 'group-container') {
                      const groupInfo = groupNodes[node.groupId];
                      const isCollapsed = groupInfo?.isCollapsed || false;
                      
                      return (
                        <div
                          key={node.id}
                          onClick={() => handleNodeClick(node)}
                          className={`absolute p-6 bg-gradient-to-r from-purple-50 to-purple-100 border-2 rounded-xl shadow-lg cursor-pointer transition-all duration-200 hover:shadow-xl ${
                            selectedNode?.id === node.id 
                              ? 'border-purple-500 ring-4 ring-purple-100 shadow-xl' 
                              : 'border-purple-200 hover:border-purple-300 hover:shadow-xl'
                          }`}
                          style={{
                            left: node.position.x,
                            top: node.position.y,
                            minWidth: '300px',
                            maxWidth: '400px'
                          }}
                        >
                          <div className="flex items-start justify-between mb-3">
                            <div className="flex items-center gap-3">
                              <h4 className="font-semibold text-purple-900 text-base leading-tight">{node.name}</h4>
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  toggleWorkspaceGroup(node.groupId);
                                }}
                                className="p-2 hover:bg-purple-200 rounded-lg transition-colors"
                                title={isCollapsed ? "Expand Group" : "Collapse Group"}
                              >
                                {isCollapsed ? <ChevronRight className="w-4 h-4 text-purple-600" /> : <ChevronDown className="w-4 h-4 text-purple-600" />}
                              </button>
                            </div>
                            <div className="flex items-center gap-2">
                              <button 
                                className="p-2 hover:bg-red-100 rounded-lg text-red-500 transition-colors"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  // Remove group container and all child nodes
                                  const childNodeIds = groupInfo?.nodeIds || [];
                                  setWorkspace(prev => prev.filter(n => n.id !== node.id && !childNodeIds.includes(n.id)));
                                  setGroupNodes(prev => {
                                    const newState = { ...prev };
                                    delete newState[node.groupId];
                                    return newState;
                                  });
                                }}
                              >
                                <Trash2 className="w-4 h-4" />
                              </button>
                            </div>
                          </div>
                          <p className="text-sm text-purple-700 mb-3 leading-relaxed">{node.description}</p>
                          <div className="text-xs text-purple-600 bg-purple-50 px-3 py-2 rounded-lg border border-purple-200">
                            {isCollapsed ? "Group collapsed" : `${groupInfo?.nodeIds?.length || 0} items in group`}
                          </div>
                        </div>
                      );
                    }

                    // Regular nodes
                    return (
                      <div
                        key={node.id}
                        onClick={() => handleNodeClick(node)}
                        className={`absolute p-6 bg-white border-2 rounded-xl shadow-lg cursor-pointer transition-all duration-200 hover:shadow-xl ${
                          selectedNode?.id === node.id 
                            ? 'border-blue-500 ring-4 ring-blue-100 shadow-xl' 
                            : 'border-gray-200 hover:border-gray-300 hover:shadow-xl'
                        } ${node.isGrouped ? 'border-l-4 border-l-purple-400' : ''}`}
                        style={{
                          left: node.position.x,
                          top: node.position.y,
                          minWidth: '300px',
                          maxWidth: '400px'
                        }}
                      >
                        <div className="flex items-start justify-between mb-3">
                          <div className="flex items-center gap-2">
                            <h4 className="font-semibold text-gray-900 text-base leading-tight">{node.name}</h4>
                            {node.isGrouped && (
                              <span className="text-xs bg-purple-100 text-purple-700 px-2 py-1 rounded-full">
                                {node.groupName}
                              </span>
                            )}
                          </div>
                          <div className="flex items-center gap-2">
                            <button className="p-2 hover:bg-gray-100 rounded-lg transition-colors">
                              <Settings className="w-4 h-4 text-gray-500" />
                            </button>
                            <button 
                              className="p-2 hover:bg-red-100 rounded-lg text-red-500 transition-colors"
                              onClick={(e) => {
                                e.stopPropagation();
                                setWorkspace(prev => prev.filter(n => n.id !== node.id));
                              }}
                            >
                              <Trash2 className="w-4 h-4" />
                            </button>
                          </div>
                        </div>
                        <p className="text-sm text-gray-600 mb-3 leading-relaxed">{node.description}</p>
                        {node.formula && (
                          <div className="text-sm text-blue-700 font-mono bg-blue-50 px-4 py-3 rounded-lg border border-blue-200">
                            {node.formula}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </div>

          {/* Action Buttons */}
          <div className="p-6 bg-white border-t border-gray-200 shadow-lg">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <button
                  onClick={handleRun}
                  disabled={workspace.length === 0}
                  className="flex items-center gap-3 px-8 py-4 bg-gradient-to-r from-green-500 to-green-600 text-white rounded-xl hover:from-green-600 hover:to-green-700 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200 shadow-lg hover:shadow-xl font-semibold text-lg"
                >
                  <Play className="w-6 h-6" />
                  Run Analysis
                </button>
                <button
                  onClick={() => setWorkspace([])}
                  disabled={workspace.length === 0}
                  className="flex items-center gap-3 px-6 py-4 bg-gradient-to-r from-red-500 to-red-600 text-white rounded-xl hover:from-red-600 hover:to-red-700 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200 shadow-lg hover:shadow-xl font-semibold"
                >
                  <Trash2 className="w-5 h-5" />
                  Clear Workspace
                </button>
              </div>
              <div className="flex items-center gap-3">
                <button className="flex items-center gap-3 px-6 py-4 bg-gradient-to-r from-blue-500 to-blue-600 text-white rounded-xl hover:from-blue-600 hover:to-blue-700 transition-all duration-200 shadow-lg hover:shadow-xl font-semibold">
                  <Save className="w-5 h-5" />
                  Save Project
                </button>
                <div className="text-sm text-gray-500">
                  {workspace.length} tool{workspace.length !== 1 ? 's' : ''} in workspace
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Right Parameter Panel */}
        {rightPanelVisible && (
          <div 
            ref={rightPanelRef}
            className="bg-white border-l border-gray-300 shadow-lg p-6 relative"
            style={{ width: `${rightPanelWidth}px` }}
          >
            <div className="flex items-center justify-between mb-6">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-blue-100 rounded-lg">
                  <Settings className="w-5 h-5 text-blue-600" />
                </div>
                <div>
                  <h3 className="text-lg font-bold text-gray-900">Node Properties</h3>
                  <p className="text-sm text-gray-600">Edit tool parameters</p>
                </div>
              </div>
              <button
                onClick={toggleRightPanel}
                className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                title="Hide Properties"
              >
                <PanelRightClose className="w-5 h-5 text-gray-500" />
              </button>
            </div>
            
            {selectedNode && (
              <div className="space-y-6">
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Tool Name
                  </label>
                  <input
                    type="text"
                    value={selectedNode.name}
                    onChange={(e) => {
                      setWorkspace(prev => prev.map(node => 
                        node.id === selectedNode.id 
                          ? { ...node, name: e.target.value }
                          : node
                      ));
                    }}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Description
                  </label>
                  <textarea
                    value={selectedNode.description}
                    onChange={(e) => {
                      setWorkspace(prev => prev.map(node => 
                        node.id === selectedNode.id 
                          ? { ...node, description: e.target.value }
                          : node
                      ));
                    }}
                    rows={4}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all resize-none"
                  />
                </div>
                
                {selectedNode.formula && (
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      Mathematical Formula
                    </label>
                    <input
                      type="text"
                      value={selectedNode.formula}
                      onChange={(e) => {
                        setWorkspace(prev => prev.map(node => 
                          node.id === selectedNode.id 
                            ? { ...node, formula: e.target.value }
                            : node
                        ));
                      }}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent font-mono text-sm transition-all"
                    />
                  </div>
                )}
                
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Position Coordinates
                  </label>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="block text-xs text-gray-500 mb-1">X Position</label>
                      <input
                        type="number"
                        value={selectedNode.position.x}
                        onChange={(e) => {
                          setWorkspace(prev => prev.map(node => 
                            node.id === selectedNode.id 
                              ? { ...node, position: { ...node.position, x: parseInt(e.target.value) || 0 } }
                              : node
                          ));
                        }}
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
                      />
                    </div>
                    <div>
                      <label className="block text-xs text-gray-500 mb-1">Y Position</label>
                      <input
                        type="number"
                        value={selectedNode.position.y}
                        onChange={(e) => {
                          setWorkspace(prev => prev.map(node => 
                            node.id === selectedNode.id 
                              ? { ...node, position: { ...node.position, y: parseInt(e.target.value) || 0 } }
                              : node
                          ));
                        }}
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
                      />
                    </div>
                  </div>
                </div>
                
                <div className="pt-4 border-t border-gray-200">
                  <div className="text-xs text-gray-500">
                    Changes are applied automatically
                  </div>
                </div>
              </div>
            )}
            
            {/* Right Panel Resize Handle */}
            <div
              className="absolute left-0 top-0 bottom-0 w-1 bg-gray-300 hover:bg-blue-500 cursor-col-resize flex items-center justify-center"
              onMouseDown={handleRightResizeStart}
            >
              <GripVertical className="w-3 h-3 text-gray-500" />
            </div>
          </div>
        )}

        {/* Right Panel Toggle Button */}
        {!rightPanelVisible && (
          <button
            onClick={toggleRightPanel}
            className="absolute right-0 top-20 z-10 p-2 bg-white border border-gray-300 rounded-l-lg shadow-lg hover:bg-gray-50 transition-colors"
            title="Show Properties"
          >
            <PanelRightOpen className="w-5 h-5 text-gray-600" />
          </button>
        )}
      </div>
    </div>
  );
};

export default ToolBoxPage; 